import {
  Table,
  TextField,
  Spin,
  getDefaultSortOrder,
} from "@pankod/refine-antd";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { getAnalytics } from "apis/analytics/getAnalytics";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getOrderNumber } from "utils";
import styles from "./styles.module.scss";

interface AnalyticsData {
  date?: string;
  dailyActiveUsers: number;
  dailyNewUsers: number;
  dailySales: number;
  dailyRevenue: number;
  conversionRate: number;
}

export const AnalyticsList: React.FC<IResourceComponentsProps> = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [analyticsList, setAnalyticsList] = useState<AnalyticsData[]>([]);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAnalytics();
      const list = Array.isArray(data?.data) ? data?.data : [];
      setAnalyticsList(list);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (date?: string): string => {
    if (!date) return "-";
    return dayjs(date).format("MM/DD/YYYY");
  };

  const tableData = useMemo(() => {
    return {
      dataSource: analyticsList,
      loading: loading,
    };
  }, [loading, analyticsList]);

  if (loading && analyticsList.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.analyticsContainer}>
      <Table
        {...tableData}
        pagination={{
          total: analyticsList.length,
          current: current,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size);
          },
        }}
        scroll={{ x: true }}
        rowKey={(record, index) => record.date || `analytics-${index}`}
      >
        <Table.Column
          dataIndex="_id"
          key="index"
          title="Index"
          width={80}
          render={(value, item, index) => (
            <TextField value={getOrderNumber(current, pageSize, index)} />
          )}
        />
        <Table.Column
          dataIndex="dailyActiveUsers"
          key="dailyActiveUsers"
          title="Daily Active Users"
          width={180}
          render={(value) => <TextField value={value || 0} />}
          defaultSortOrder={getDefaultSortOrder("dailyActiveUsers")}
          sorter
        />
        <Table.Column
          dataIndex="dailyNewUsers"
          key="dailyNewUsers"
          title="Daily New Users"
          width={160}
          render={(value) => <TextField value={value || 0} />}
          defaultSortOrder={getDefaultSortOrder("dailyNewUsers")}
          sorter
        />
        <Table.Column
          dataIndex="dailySales"
          key="dailySales"
          title="Daily Sales"
          width={140}
          render={(value) => <TextField value={value || 0} />}
          defaultSortOrder={getDefaultSortOrder("dailySales")}
          sorter
        />
        <Table.Column
          dataIndex="dailyRevenue"
          key="dailyRevenue"
          title="Daily Revenue"
          width={160}
          render={(value) => (
            <TextField value={formatCurrency(value || 0)} />
          )}
          defaultSortOrder={getDefaultSortOrder("dailyRevenue")}
          sorter
        />
        <Table.Column
          dataIndex="conversionRate"
          key="conversionRate"
          title="Conversion Rate"
          width={160}
          render={(value) => (
            <TextField value={formatPercentage(value || 0)} />
          )}
          defaultSortOrder={getDefaultSortOrder("conversionRate")}
          sorter
        />
        <Table.Column
          dataIndex="date"
          key="date"
          title="Date"
          width={150}
          render={(value) => <TextField value={formatDate(value)} />}
          defaultSortOrder={getDefaultSortOrder("date")}
          sorter
        />
      </Table>
    </div>
  );
};

